import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  TextField, 
  Button, 
  Box, 
  Alert, 
  Typography, 
  Paper, 
  CircularProgress,
  Link
} from '@mui/material';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import AuthHeaderActions from '../../components/auth/AuthHeaderActions';
import PrimaryActionButton from '../../components/common/PrimaryActionButton';
import SalovareLogo from '../../components/common/SalovareLogo';
import AuthService from '../../services/authService';
import PasswordField from '../../components/auth/PasswordField';
import useLocalizedMarketingPath from '../../hooks/useLocalizedMarketingPath';
import LinkSentConfirmation from '../../components/auth/LinkSentConfirmation';
import { BRAND, brandAlpha } from '../../theme/brandTokens';
import { brandBackdropSx, brandCardSx } from '../../theme/brandSurfaces';

const backgroundStyle = {
  minHeight: { xs: '100dvh', sm: '100vh' },
  width: '100%',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  ...brandBackdropSx,
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  overflowY: 'auto',
  overflowX: 'hidden',
  WebkitOverflowScrolling: 'touch',
  py: { xs: 2, sm: 0 },
  px: 0,
  touchAction: 'pan-y',
};

const cardStyle = {
  padding: { xs: '2rem 1.5rem', sm: '2.5rem 2rem' },
  ...brandCardSx,
  minWidth: { xs: 300, sm: 340 },
  maxWidth: { xs: 350, sm: 380 },
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
};

const TeamMemberLoginPage = () => {
  const { t } = useTranslation();
  const { loginTeamMember } = useAuth();
  const navigate = useNavigate();
  const localizePath = useLocalizedMarketingPath();
  const location = useLocation();
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [setPasswordLoading, setSetPasswordLoading] = useState(false);
  const [setPasswordSuccess, setSetPasswordSuccess] = useState(false);
  const registrationSuccess = location.state?.registrationSuccess;
  const from = new URLSearchParams(location.search).get('redirect') ||
               location.state?.from?.pathname || 
               '/management';

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (error && error.startsWith('forgotPassword.')) {
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (error && error.startsWith('forgotPassword.')) {
      return;
    }
    
    setError('');
    if (!form.email || !form.password) {
      setError('login.required');
      return;
    }
    setLoading(true);
    const result = await loginTeamMember(form);
    setLoading(false);
    if (result.success) {
      const redirectPath = from && from !== '/management/team-member/login' ? from : '/management';
      setTimeout(() => {
        navigate(redirectPath, { replace: true });
      }, 50);
    } else {
      // Show appropriate error message based on error type
      setError(result.errorKey ? result.errorKey : result.message || 'login.invalid');
    }
  };

  const handleForgotPassword = async () => {
    if (!form.email) {
      setError('forgotPassword.emailRequired');
      return;
    }
    
    setResetLoading(true);
    setError('');
    
    try {
      await AuthService.requestPasswordResetTeamMember(form.email);
      setResetSuccess(true);
    } catch (error) {
      setError('forgotPassword.error');
    } finally {
      setResetLoading(false);
    }
  };

  const handleFirstAccess = async () => {
    if (!form.email) {
      setError('firstAccess.emailRequired');
      return;
    }
    
    setSetPasswordLoading(true);
    setError('');
    
    try {
      await AuthService.requestPasswordSetTeamMember(form.email);
      setSetPasswordSuccess(true);
    } catch (error) {
      setError('firstAccess.error');
    } finally {
      setSetPasswordLoading(false);
    }
  };

  return (
    <Box sx={backgroundStyle}>
      <Box sx={{ display: { xs: 'none', sm: 'block' }, position: 'absolute', top: 24, right: 24 }}>
        <AuthHeaderActions />
      </Box>
      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, py: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2 } }}>
        <Box sx={{ display: { xs: 'flex', sm: 'none' }, width: '100%', maxWidth: 380, justifyContent: 'flex-end', alignItems: 'center', px: 3, pt: 2, pb: 2, minHeight: 56, boxSizing: 'border-box' }}>
          <AuthHeaderActions />
        </Box>
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', pt: { xs: 0, sm: 0 } }}>
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: 'easeOut' }}
      >
        <Paper elevation={8} sx={cardStyle}>
          <SalovareLogo />
          {registrationSuccess && (
            <Alert severity="success" sx={{ width: '100%', mb: 2, borderRadius: 2 }}>
              {t('login.storeRegistrationSuccess')}
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error.startsWith('login.') || error.startsWith('forgotPassword.') || error.startsWith('firstAccess.') || error.startsWith('stores.')
                ? t(error)
                : error}
            </Alert>
          )}
          {resetSuccess ? (
            <LinkSentConfirmation
              message={t('forgotPassword.success')}
              email={form.email}
              onResend={handleForgotPassword}
              onBackToLogin={() => setResetSuccess(false)}
            />
          ) : setPasswordSuccess ? (
            <LinkSentConfirmation
              message={t('firstAccess.success')}
              email={form.email}
              onResend={handleFirstAccess}
              onBackToLogin={() => setSetPasswordSuccess(false)}
            />
          ) : (
          <Box component="form" noValidate onSubmit={handleSubmit} sx={{ width: '100%', px: 1 }}>
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexWrap: 'wrap',
                gap: 1,
                mb: 3,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: BRAND.textMuted,
                  fontWeight: 500,
                  textAlign: 'center',
                }}
              >
                {t('login.registerStorePrompt')}
              </Typography>
              <Button
                type="button"
                variant="outlined"
                size="small"
                onClick={() => navigate(localizePath('/register-store'))}
                sx={{
                  minWidth: 'auto',
                  px: 1.5,
                  py: 0.45,
                  borderRadius: 999,
                  textTransform: 'none',
                  fontWeight: 800,
                  fontSize: '0.82rem',
                  color: BRAND.accentText,
                  borderColor: BRAND.line,
                  backgroundColor: BRAND.surface,
                  '&:hover': {
                    borderColor: BRAND.accent,
                    backgroundColor: brandAlpha('accent', 0.08),
                  },
                }}
              >
                {t('login.registerStoreCta')}
              </Button>
            </Box>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label={t('login.email')}
              name="email"
              autoComplete="email"
              autoFocus
              value={form.email}
              onChange={handleChange}
              sx={{ mb: 2, borderRadius: 2 }}
              InputProps={{
                style: { borderRadius: 12 },
              }}
            />
            <PasswordField
              margin="normal"
              required
              fullWidth
              name="password"
              label={t('login.password')}
              id="password"
              autoComplete="current-password"
              value={form.password}
              onChange={handleChange}
              sx={{ mb: 1, borderRadius: 2 }}
              InputProps={{
                style: { borderRadius: 12 },
              }}
            />
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between', 
              alignItems: { xs: 'stretch', sm: 'center' },
              gap: { xs: 1, sm: 0 },
              mb: 2 
            }}>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={handleFirstAccess}
                disabled={setPasswordLoading}
                sx={{
                  color: '#28a745',
                  textDecoration: 'none',
                  fontSize: { xs: '0.875rem', sm: '0.875rem' },
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                  '&:disabled': {
                    color: BRAND.textSubtle,
                    cursor: 'not-allowed',
                  },
                }}
              >
                {setPasswordLoading ? t('firstAccess.sending') : t('firstAccess.firstAccess')}
              </Link>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={handleForgotPassword}
                disabled={resetLoading}
                sx={{
                  color: BRAND.accentText,
                  textDecoration: 'none',
                  fontSize: { xs: '0.875rem', sm: '0.875rem' },
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                  '&:disabled': {
                    color: BRAND.textSubtle,
                    cursor: 'not-allowed',
                  },
                }}
              >
                {resetLoading ? t('forgotPassword.sending') : t('forgotPassword.forgotPassword')}
              </Link>
            </Box>
            <PrimaryActionButton
              type="submit"
              fullWidth
              sx={{
                mt: 2,
                mb: 0,
                py: 1.5,
                fontSize: '1.1rem',
              }}
              disabled={loading}
              endIcon={loading && <CircularProgress size={22} color="inherit" />}
            >
              {loading ? t('login.signingIn') : t('login.signIn')}
            </PrimaryActionButton>
            
            {/* Admin login link */}
            <Box sx={{ textAlign: 'right', width: '100%' }}>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => navigate(`/management/app-admin/login${location.search}`)}
                sx={{
                  color: BRAND.textSubtle,
                  textDecoration: 'none',
                  fontSize: '0.7rem',
                  fontWeight: 400,
                  minHeight: 'auto',
                  padding: 0,
                  '&:hover': {
                    textDecoration: 'underline',
                    color: BRAND.accentText,
                  },
                }}
              >
                {t('login.adminQuestion')}
              </Link>
            </Box>
          </Box>
          )}
        </Paper>
      </motion.div>
        </Box>
      </Box>
    </Box>
  );
};

export default TeamMemberLoginPage;

